/**
 * <db-popup-component>: the 10% welcome offer.
 *
 * Never opens on page load. Opens once the shopper has scrolled past half the page AND spent 25 seconds
 * here, or on desktop exit intent after 15 seconds. Never on /cart, never while the cart drawer is open
 * or within 5 seconds of it closing, never while a privacy banner is on screen, never for shoppers who
 * arrived through a /discount/ link, never while a purchase box (or the closing add button) is in view or within 5 seconds
 * of it leaving the screen. A dismissal silences it for 14 days; a signup silences it for good.
 *
 * The signup is a normal form post, not fetch: Shopify's CDN answers background POSTs to /contact with a
 * bot check (403 "Verifying your connection") that only a real navigation can pass. Shopify redirects back
 * to the page with ?customer_posted=true#db-popup-form, and the section renders a marker for the result;
 * this component then reopens the dialog on the success (or failure) view.
 *
 * The game contract (v2): while `html[data-db-playing]` is set (the play page, the arena, a run in progress) the popup
 * never opens by itself, and once a shopper has revealed the code in the game (`db-code-revealed`) it stays silent for
 * them. Any page can still open it on request with `show()` (the play page's "email me the code" button), except while a
 * run is in progress (`data-db-playing="run"`). Every open, suppression and signup is reported as a db_* event.
 */
import { Component } from '@theme/component';
import { emit, emitOnce } from '@theme/db-events';

const DISMISS_KEY = 'db-popup-dismissed';
const DONE_KEY = 'db-popup-done';
const REVEALED_KEY = 'db-code-revealed';
const DISMISS_DAYS = 14;
const MIN_TIME_SCROLL_MS = 25000;
const MIN_TIME_EXIT_MS = 15000;
const FORM_HASH = '#db-popup-form';
const BUY_GRACE_MS = 5000;
/** The elements that mean the shopper is choosing masks right now. */
const BUY_SELECTOR = 'db-buybox-component, db-cta-component';

/** @param {string} key */
function read(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** @param {string} key @param {string} value */
function write(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage can be blocked */
  }
}

const PENDING_KEY = 'db-popup-pending';

/** Per-tab note that this tab submitted the popup form and is waiting to come back from Shopify. */
function takePending() {
  try {
    const pending = sessionStorage.getItem(PENDING_KEY) === '1';
    sessionStorage.removeItem(PENDING_KEY);
    return pending;
  } catch {
    return false;
  }
}

function markPending() {
  try {
    sessionStorage.setItem(PENDING_KEY, '1');
  } catch {
    /* storage can be blocked: the URL hash still identifies the form */
  }
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * @typedef {Object} PopupRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} formView
 * @property {HTMLElement} doneView
 * @property {HTMLElement} title
 * @property {HTMLElement} doneTitle
 * @property {HTMLInputElement} email
 * @property {HTMLElement} error
 * @property {HTMLButtonElement} submit
 * @property {HTMLButtonElement} copy
 *
 * @extends {Component<PopupRefs>}
 */
class DbPopupComponent extends Component {
  requiredRefs = ['dialog'];

  #abort = new AbortController();
  #started = Date.now();
  #shown = false;
  /** @type {Element | null} */
  #opener = null;
  #drawerClosedAt = 0;
  /** @type {Set<Element>} purchase boxes currently on screen */
  #buying = new Set();
  #buyLeftAt = 0;
  /** @type {IntersectionObserver | undefined} */
  #buyObserver;
  #submitting = false;
  #steppingAside = false;
  #submitLabel = '';
  /** @type {number | undefined} */
  #asideTimer;

  connectedCallback() {
    super.connectedCallback();
    // Back from Shopify after our own form post? The URL hash (or this tab's pending note) tells our form
    // apart from any other customer form on the site.
    const wasPending = takePending();
    const fromOurForm = location.hash === FORM_HASH || wasPending;
    const posted = fromOurForm && this.querySelector('[data-db-popup-posted]') !== null;
    const failed = fromOurForm && !posted && this.querySelector('[data-db-popup-failed]') !== null;
    if (posted) {
      write(DONE_KEY, '1');
      emitOnce('db_popup_signup');
    }
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    // the dialog always works (show() needs it); only the timed and exit-intent triggers are switched off when suppressed
    const form = this.querySelector('form');
    form?.addEventListener('submit', this.#onSubmit, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    this.refs.dialog.addEventListener('close', this.#onClose, { signal });
    this.refs.dialog.addEventListener('click', this.#onBackdrop, { signal });

    if (posted || failed) {
      this.#showResult(posted);
      return;
    }
    if (this.#suppressed()) return;

    document.addEventListener('scroll', this.#maybeOpenOnScroll, { passive: true, capture: true, signal });
    document.addEventListener('mouseout', this.#onMouseOut, { signal });
    document.addEventListener('theme-drawer:close', () => (this.#drawerClosedAt = Date.now()), { signal });
    this.#watchBuyBoxes();
  }

  /** Keeps `#buying` in step with the purchase boxes on screen, so the timed and exit-intent triggers wait. */
  #watchBuyBoxes() {
    if (!('IntersectionObserver' in window)) return;
    this.#buyObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#buying.add(entry.target);
        } else if (this.#buying.delete(entry.target)) {
          this.#buyLeftAt = Date.now();
        }
      }
    });
    for (const el of document.querySelectorAll(BUY_SELECTOR)) this.#buyObserver.observe(el);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#buyObserver?.disconnect();
    window.clearTimeout(this.#asideTimer);
  }

  #suppressed() {
    if (read(REVEALED_KEY)) {
      emitOnce('db_popup_blocked', { reason: 'code_revealed' }, 'popup-blocked-code_revealed');
      return true;
    }
    if (read(DONE_KEY)) return true;
    const dismissed = Number(read(DISMISS_KEY));
    if (dismissed && Date.now() - dismissed < DISMISS_DAYS * 86400000) return true;
    // arrived through a /discount/ link: they already have a code
    if (/(?:^|;\s*)discount_code=/.test(document.cookie)) return true;
    return false;
  }

  #blocked() {
    if (this.#shown) return true;
    if (document.documentElement.hasAttribute('data-db-playing')) {
      emitOnce('db_popup_blocked', { reason: 'playing' }, 'popup-blocked-playing');
      return true;
    }
    if (location.pathname.startsWith('/cart')) return true;
    if (document.querySelector('theme-drawer[open], theme-drawer dialog[open]')) return true;
    if (Date.now() - this.#drawerClosedAt < 5000) return true;
    if (this.#buying.size > 0 || Date.now() - this.#buyLeftAt < BUY_GRACE_MS) {
      emitOnce('db_popup_blocked', { reason: 'buy_box' }, 'popup-blocked-buy_box');
      return true;
    }
    if (document.querySelector('#shopify-pc__banner, shopify-privacy-banner')) {
      const banner = document.querySelector('#shopify-pc__banner');
      if (banner instanceof HTMLElement && banner.offsetParent !== null) return true;
    }
    if (document.querySelector('dialog[open]')) return true;
    return false;
  }

  #scrollFraction() {
    const wrapper = document.querySelector('.page-wrapper');
    const useWrapper = wrapper instanceof HTMLElement && wrapper.scrollHeight > wrapper.clientHeight + 2 && window.scrollY === 0;
    const top = useWrapper ? wrapper.scrollTop : window.scrollY;
    const view = useWrapper ? wrapper.clientHeight : window.innerHeight;
    const total = useWrapper ? wrapper.scrollHeight : document.documentElement.scrollHeight;
    return (top + view) / total;
  }

  #maybeOpenOnScroll = () => {
    if (this.#blocked()) return;
    if (Date.now() - this.#started < MIN_TIME_SCROLL_MS) return;
    if (this.#scrollFraction() < 0.5) return;
    this.#open('scroll');
  };

  /** @param {MouseEvent} event */
  #onMouseOut = (event) => {
    if (this.#blocked() || event.relatedTarget || event.clientY > 0) return;
    if (!window.matchMedia('(hover: hover)').matches) return;
    if (Date.now() - this.#started < MIN_TIME_EXIT_MS) return;
    this.#open('exit');
  };

  /** @param {string} trigger - what opened it, for the db_popup_open event */
  #open(trigger) {
    const { dialog, title } = this.refs;
    if (this.#shown || typeof dialog.showModal !== 'function') return;
    this.#shown = true;
    this.#opener = document.activeElement;
    dialog.showModal();
    title?.focus();
    emit('db_popup_open', { trigger });
  }

  /**
   * Opens the offer now, whatever the timers and earlier dismissals say. A shopper who already has the code sees it.
   * Does nothing while a game run is in progress.
   */
  show() {
    if (document.documentElement.getAttribute('data-db-playing') === 'run') return;
    const { formView, doneView } = this.refs;
    if (read(DONE_KEY) && formView && doneView) {
      formView.hidden = true;
      doneView.hidden = false;
    }
    this.#shown = false;
    this.#open('button');
  }

  close() {
    this.refs.dialog.close();
  }

  /** @param {Event} event */
  #onBackdrop = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };

  #onClose = () => {
    // stepping aside for Shopify's spam check is not a dismissal: leave focus alone for the check and keep the offer alive
    if (this.#steppingAside) return;
    if (!read(DONE_KEY)) write(DISMISS_KEY, String(Date.now()));
    if (this.#opener instanceof HTMLElement) this.#opener.focus();
  };

  async copyCode() {
    const { copy } = this.refs;
    try {
      await navigator.clipboard.writeText(this.dataset.code ?? '');
      copy.textContent = this.dataset.tCopied ?? '';
      window.setTimeout(() => (copy.textContent = this.dataset.tCopy ?? ''), 1800);
    } catch {
      /* clipboard can be unavailable; the code is on screen */
    }
  }

  /**
   * Reopens the dialog on the result of the form post that just reloaded the page.
   * @param {boolean} posted - true when Shopify accepted the signup, false when it reported errors.
   */
  #showResult(posted) {
    const { error, formView, doneView, doneTitle } = this.refs;
    if (posted) {
      formView.hidden = true;
      doneView.hidden = false;
    } else {
      error.textContent = this.dataset.tNetwork ?? '';
      error.hidden = false;
    }
    // drop ?customer_posted=true and the form hash so a refresh or a shared link does not replay the result
    const url = new URL(location.href);
    url.searchParams.delete('customer_posted');
    if (url.hash === FORM_HASH) url.hash = '';
    history.replaceState(history.state, '', url);
    // open after the first paint so the dialog never competes with page load
    requestAnimationFrame(() => {
      this.#open('result');
      if (posted) doneTitle.focus();
    });
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const { email, error, submit } = this.refs;
    const value = email.value.trim();
    error.hidden = true;
    email.removeAttribute('aria-invalid');
    if (!value || !EMAIL.test(value)) {
      event.preventDefault();
      error.textContent = value ? (this.dataset.tInvalid ?? '') : (this.dataset.tEmpty ?? '');
      error.hidden = false;
      email.setAttribute('aria-invalid', 'true');
      email.focus();
      return;
    }
    if (this.#submitting) {
      event.preventDefault();
      return;
    }
    // valid: let the browser post the form to Shopify (it can pass the bot check; fetch cannot)
    this.#submitting = true;
    markPending();
    email.value = value;
    this.#submitLabel = submit.textContent ?? '';
    submit.textContent = this.dataset.tSending ?? this.#submitLabel;
    submit.setAttribute('aria-busy', 'true');
    window.setTimeout(() => (submit.disabled = true), 0);
    // Shopify may answer with a visible spam check (hCaptcha) that lands on the page behind this modal dialog,
    // where it is dimmed and inert. If the page has not moved on shortly, step aside so the shopper can solve it.
    this.#asideTimer = window.setTimeout(() => this.#stepAside(), 1500);
  };

  #stepAside() {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    this.#steppingAside = true;
    dialog.close();
  }

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    // coming back through the browser's back button restores the page as it was left: reset the form
    if (!event.persisted) return;
    const { submit } = this.refs;
    window.clearTimeout(this.#asideTimer);
    takePending();
    this.#submitting = false;
    this.#steppingAside = false;
    submit.disabled = false;
    submit.removeAttribute('aria-busy');
    if (this.#submitLabel) submit.textContent = this.#submitLabel;
  };
}

if (!customElements.get('db-popup-component')) {
  customElements.define('db-popup-component', DbPopupComponent);
}
